import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, XIcon, BrainIcon, HeadphonesIcon, CalendarIcon, MailIcon, SparklesIcon, ZapIcon, ShieldCheckIcon, RocketIcon, CrownIcon } from 'lucide-react';
export function ProtocoloEliteSection() {
  const goldColor = '#D4A853';
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const eliteTomos = [{
    icon: <BrainIcon className="w-5 h-5 text-white" data-id="element-297" />,
    title: 'IA-BRAIN',
    description: '5 Super Prompts para resumir cualquier texto con IA en minutos',
    color: '#D4A853',
    shadowColor: 'rgba(212, 168, 83, 0.5)'
  }, {
    icon: <HeadphonesIcon className="w-5 h-5 text-white" data-id="element-298" />,
    title: 'FOCO DE ACERO',
    description: '5 tecnicas para activar el foco profundo antes de estudiar',
    color: '#8B5CF6',
    shadowColor: 'rgba(139, 92, 246, 0.5)'
  }, {
    icon: <CalendarIcon className="w-5 h-5 text-white" data-id="element-299" />,
    title: 'COMANDO DE EXÁMENES',
    description: 'Planificador en Google Sheets para llegar preparado a cada parcial',
    color: '#10B981',
    shadowColor: 'rgba(16, 185, 129, 0.5)'
  }, {
    icon: <MailIcon className="w-5 h-5 text-white" data-id="element-300" />,
    title: 'DIPLOMACIA UNIVERSITARIA',
    description: '10 plantillas de mails y WhatsApp para hablar con profesores',
    color: '#F43F5E',
    shadowColor: 'rgba(244, 63, 94, 0.5)'
  }];
  const eliteBenefits = [{
    icon: <ZapIcon className="w-5 h-5" data-id="element-301" />,
    text: 'Resumís cualquier texto en minutos con IA'
  }, {
    icon: <HeadphonesIcon className="w-5 h-5" data-id="element-302" />,
    text: 'Activás el foco profundo antes de cada sesión de estudio'
  }, {
    icon: <CalendarIcon className="w-5 h-5" data-id="element-303" />,
    text: 'Planificás cada parcial sin improvisar'
  }, {
    icon: <ShieldCheckIcon className="w-5 h-5" data-id="element-304" />,
    text: 'Te comunicás con profesores de forma profesional'
  }, {
    icon: <RocketIcon className="w-5 h-5" data-id="element-305" />,
    text: 'Vas un paso adelante que el resto de tus compañeros'
  }];
  return <section className="w-full py-24 px-4 bg-transparent relative overflow-hidden font-sans" data-id="element-306">
      {/* Background Glows */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-[400px] bg-[#D4A853]/5 blur-[120px] rounded-full pointer-events-none" data-id="element-307" />

      <div className="max-w-4xl mx-auto flex flex-col items-center relative z-10" data-id="element-308">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="w-full flex flex-col items-center" data-id="element-309">
          {/* BLOQUE 1 — PREGUNTA DE APERTURA */}
          <motion.div variants={itemVariants} className="flex flex-col items-center text-center mb-16" data-id="element-310">
            <div className="flex items-center gap-3 mb-6" data-id="element-311">
              <CrownIcon className="w-8 h-8 text-[#D4A853] drop-shadow-[0_0_10px_rgba(212,168,83,0.5)]" data-id="element-312" />
              <span className="text-[#D4A853] font-black text-sm md:text-base tracking-widest uppercase border border-[#D4A853]/30 bg-[#D4A853]/10 px-4 py-1.5 rounded-full" data-id="element-313">
                UPGRADE EXCLUSIVO
              </span>
            </div>

            <h2 className="text-3xl md:text-5xl font-black text-white mb-6 leading-tight" data-id="element-314">
              ¿Querés ir un paso más allá?
              <br data-id="element-315" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4A853] to-[#F5D78E]" data-id="element-316">
                Conocé el Protocolo Elite
              </span>
            </h2>

            <p className="text-lg md:text-xl text-textMuted max-w-2xl font-medium leading-relaxed" data-id="element-317">
              4 herramientas avanzadas que te dan una ventaja real sobre el
              resto. Solo disponible con el{' '}
              <span className="text-white font-bold" data-id="element-318">MegaPack PRO</span>.
            </p>
          </motion.div>

          {/* BLOQUE 2 — 4 CARDS DE TOMOS */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full mb-16" data-id="element-319">
            {eliteTomos.map((tomo, index) => <div key={index} className="group relative bg-[#130F1C] rounded-xl p-4 md:p-5 border border-white/5 overflow-hidden w-full transition-all duration-500 hover:-translate-y-1" style={{
            '--hover-color': tomo.color
          } as React.CSSProperties} data-id="element-320">
                {/* Borde iluminado en hover */}
                <div className="absolute inset-0 rounded-xl border-2 border-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
              borderColor: tomo.color,
              boxShadow: `inset 0 0 20px ${tomo.shadowColor}`
            }} data-id="element-321" />

                {/* Resplandor inferior */}
                <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 w-[80%] h-[60px] blur-[40px] opacity-40 group-hover:opacity-80 group-hover:h-[100px] transition-all duration-500 pointer-events-none" style={{
              backgroundColor: tomo.color
            }} data-id="element-322" />

                <div className="relative z-10" data-id="element-323">
                  <div className="flex justify-between items-center mb-3" data-id="element-324">
                    {/* Icono */}
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{
                  backgroundColor: tomo.color
                }} data-id="element-325">
                      {tomo.icon}
                    </div>

                    {/* Pill */}
                    <div className="px-3 py-1 rounded-full border border-white/10 bg-white/5 text-xs font-black text-white/80 tracking-widest uppercase" data-id="element-326">
                      TOMO {index + 1}
                    </div>
                  </div>

                  {/* Título */}
                  <h3 className="text-lg md:text-xl font-black text-white tracking-tight mb-1" data-id="element-327">
                    {tomo.title}
                  </h3>

                  {/* Descripción */}
                  <p className="text-textMuted text-sm leading-snug" data-id="element-328">
                    {tomo.description}
                  </p>
                </div>
              </div>)}
          </motion.div>

          {/* BLOQUE 3 — 5 BENEFICIOS CON CHECKMARKS */}
          <motion.div variants={itemVariants} className="w-full max-w-2xl bg-cardBg/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-[#D4A853]/20 mb-16" data-id="element-329">
            <h3 className="text-xl md:text-2xl font-black text-white mb-6 text-center" data-id="element-330">
              Con el Protocolo Elite vas a poder:
            </h3>
            <div className="flex flex-col gap-4" data-id="element-331">
              {eliteBenefits.map((benefit, index) => <div key={index} className="flex items-start gap-3" data-id="element-332">
                  <div className="w-6 h-6 rounded-full bg-[#D4A853] flex items-center justify-center shrink-0 mt-0.5 shadow-[0_0_10px_rgba(212,168,83,0.3)]" data-id="element-333">
                    <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-334" />
                  </div>
                  <p className="text-white/90 text-base font-medium leading-snug" data-id="element-335">
                    {benefit.text}
                  </p>
                </div>)}
            </div>
          </motion.div>

          {/* BLOQUE 4 — COMPARACIÓN MEGAPACK vs MEGAPACK PRO */}
          <motion.div variants={itemVariants} className="w-full max-w-2xl grid grid-cols-1 md:grid-cols-2 gap-4 mb-16" data-id="element-336">
            {/* MegaPack Básico */}
            <div className="bg-cardBg/60 rounded-2xl p-5 md:p-6 border border-white/10" data-id="element-337">
              <h4 className="text-lg font-black text-white mb-1" data-id="element-338">
                MEGA PACK UNIVERSITARIO
              </h4>
              <div className="flex items-baseline gap-2 mb-5" data-id="element-339">
                <span className="text-white/40 line-through text-sm font-bold" data-id="element-340">
                  $39.998
                </span>
                <span className="text-2xl font-black text-white" data-id="element-341">$19.999</span>
              </div>
              <ul className="flex flex-col gap-3" data-id="element-342">
                {[{
                text: '5 Tomos de estudio',
                included: true
              }, {
                text: '3 Bonos estratégicos',
                included: true
              }, {
                text: 'IA-BRAIN (resúmenes con IA)',
                included: false
              }, {
                text: 'FOCO DE ACERO (audios)',
                included: false
              }, {
                text: 'COMANDO DE EXÁMENES',
                included: false
              }, {
                text: 'DIPLOMACIA UNIVERSITARIA',
                included: false
              }].map((item, i) => <li key={i} className={`flex items-start gap-2 text-sm ${item.included ? 'text-white/90' : 'text-white/30'}`} data-id="element-343">
                    {item.included ? <CheckIcon className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-344" /> : <XIcon className="w-4 h-4 text-red-400/50 shrink-0 mt-0.5" data-id="element-345" />}
                    {item.text}
                  </li>)}
              </ul>
            </div>

            {/* MegaPack PRO */}
            <div className="bg-[#D4A853]/5 rounded-2xl p-5 md:p-6 border-2 border-[#D4A853]/40 shadow-[0_0_30px_rgba(212,168,83,0.1)] relative" data-id="element-346">
              <div className="absolute -top-3 right-4 bg-gradient-to-r from-gradOrange to-gradPink text-white text-[10px] md:text-xs font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-[0_0_15px_rgba(255,107,0,0.4)]" data-id="element-347">
                ⭐ MÁS ELEGIDO
              </div>
              <h4 className="text-lg font-black text-[#D4A853] mb-1" data-id="element-348">
                MEGAPACK PRO
              </h4>
              <div className="flex items-baseline gap-2 mb-5" data-id="element-349">
                <span className="text-white/40 line-through text-sm font-bold" data-id="element-350">
                  $49.998
                </span>
                <span className="text-2xl font-black text-white" data-id="element-351">$27.999</span>
              </div>
              <ul className="flex flex-col gap-3" data-id="element-352">
                {['5 Tomos de estudio', '3 Bonos estratégicos', 'IA-BRAIN (resúmenes con IA)', 'FOCO DE ACERO (audios)', 'COMANDO DE EXÁMENES', 'DIPLOMACIA UNIVERSITARIA'].map((item, i) => <li key={i} className="flex items-start gap-2 text-white/90 text-sm" data-id="element-353">
                    <CheckIcon className="w-4 h-4 text-[#D4A853] shrink-0 mt-0.5" data-id="element-354" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* BLOQUE 5 — CTA */}
          <motion.div variants={itemVariants} className="flex flex-col items-center text-center" data-id="element-355">
            <p className="text-lg md:text-xl text-textMuted font-medium mb-6" data-id="element-356">
              Accedé al Protocolo Elite eligiendo el{' '}
              <span className="text-white font-bold" data-id="element-357">MegaPack PRO</span>
            </p>
            <a href="#elegir-opcion" className="bg-gradient-to-r from-[#10B981] to-[#047857] hover:from-[#059669] hover:to-[#065f46] text-white font-black text-base md:text-lg px-6 py-2.5 rounded-full transition-all duration-300 shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] hover:scale-105 inline-block text-center no-underline whitespace-nowrap" data-id="element-358">
              Quiero el Protocolo Elite — $27.999
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}